export type TtsVoiceGender = "male" | "female";
export type TtsVoiceProvider = "gemini" | "openai";

export interface TtsVoiceOption {
	id: string;
	provider: TtsVoiceProvider;
	gender: TtsVoiceGender;
	/** Short character tag from provider docs (e.g. "Breezy"). */
	style?: string;
	/** Public preview WAV/MP3 when available. */
	sampleUrl?: string;
}

const GEMINI_SAMPLE_BASE =
	"https://www.gstatic.com/aistudio/voices/samples";

/**
 * Active TTS engine for video projects.
 * Default: gemini. Set VIDEO_PROJECT_TTS_PROVIDER or
 * NEXT_PUBLIC_VIDEO_PROJECT_TTS_PROVIDER to "openai" to switch the whole app.
 */
export function getVideoProjectTtsProvider(): TtsVoiceProvider {
	const raw = (
		process.env.VIDEO_PROJECT_TTS_PROVIDER ||
		process.env.NEXT_PUBLIC_VIDEO_PROJECT_TTS_PROVIDER ||
		"gemini"
	)
		.trim()
		.toLowerCase();
	return raw === "openai" ? "openai" : "gemini";
}

/** True only when env switches TTS to OpenAI. */
export function isVideoProjectOpenaiTtsEnabled(): boolean {
	return getVideoProjectTtsProvider() === "openai";
}

/** Always the configured provider — no per-character / narrator mixing. */
export function coerceVideoProjectTtsProvider(
	_requested?: string | null,
): TtsVoiceProvider {
	return getVideoProjectTtsProvider();
}

function geminiVoice(
	id: string,
	gender: TtsVoiceGender,
	style: string,
): TtsVoiceOption {
	return {
		id,
		provider: "gemini",
		gender,
		style,
		sampleUrl: `${GEMINI_SAMPLE_BASE}/${id}.wav`,
	};
}

/**
 * Full Gemini TTS prebuilt voice set (30).
 * Gender from Cloud Text-to-Speech Gemini-TTS docs.
 */
export const GEMINI_TTS_VOICE_OPTIONS: readonly TtsVoiceOption[] = [
	geminiVoice("Achernar", "female", "Soft"),
	geminiVoice("Achird", "male", "Friendly"),
	geminiVoice("Algenib", "male", "Gravelly"),
	geminiVoice("Algieba", "male", "Smooth"),
	geminiVoice("Alnilam", "male", "Firm"),
	geminiVoice("Aoede", "female", "Breezy"),
	geminiVoice("Autonoe", "female", "Bright"),
	geminiVoice("Callirrhoe", "female", "Easy-going"),
	geminiVoice("Charon", "male", "Informative"),
	geminiVoice("Despina", "female", "Smooth"),
	geminiVoice("Enceladus", "male", "Breathy"),
	geminiVoice("Erinome", "female", "Clear"),
	geminiVoice("Fenrir", "male", "Excitable"),
	geminiVoice("Gacrux", "female", "Mature"),
	geminiVoice("Iapetus", "male", "Clear"),
	geminiVoice("Kore", "female", "Firm"),
	geminiVoice("Laomedeia", "female", "Upbeat"),
	geminiVoice("Leda", "female", "Youthful"),
	geminiVoice("Orus", "male", "Firm"),
	geminiVoice("Puck", "male", "Upbeat"),
	geminiVoice("Pulcherrima", "female", "Forward"),
	geminiVoice("Rasalgethi", "male", "Informative"),
	geminiVoice("Sadachbia", "male", "Lively"),
	geminiVoice("Sadaltager", "male", "Knowledgeable"),
	geminiVoice("Schedar", "male", "Even"),
	geminiVoice("Sulafat", "female", "Warm"),
	geminiVoice("Umbriel", "male", "Easy-going"),
	geminiVoice("Vindemiatrix", "female", "Gentle"),
	geminiVoice("Zephyr", "female", "Bright"),
	geminiVoice("Zubenelgenubi", "male", "Casual"),
];

/**
 * OpenAI TTS voices. Gender is approximate.
 * Used only when VIDEO_PROJECT_TTS_PROVIDER=openai.
 */
export const OPENAI_TTS_VOICE_OPTIONS: readonly TtsVoiceOption[] = [
	{ id: "alloy", provider: "openai", gender: "female", style: "Neutral" },
	{ id: "ash", provider: "openai", gender: "male", style: "Warm" },
	{ id: "ballad", provider: "openai", gender: "male", style: "Clear" },
	{ id: "coral", provider: "openai", gender: "female", style: "Bright" },
	{ id: "echo", provider: "openai", gender: "male", style: "Deep" },
	{ id: "fable", provider: "openai", gender: "female", style: "Animated" },
	{ id: "nova", provider: "openai", gender: "female", style: "Upbeat" },
	{ id: "onyx", provider: "openai", gender: "male", style: "Bold" },
	{ id: "sage", provider: "openai", gender: "female", style: "Calm" },
	{ id: "shimmer", provider: "openai", gender: "female", style: "Soft" },
	{ id: "verse", provider: "openai", gender: "male", style: "Expressive" },
	{ id: "marin", provider: "openai", gender: "female", style: "Clear" },
	{ id: "cedar", provider: "openai", gender: "male", style: "Warm" },
];

/** @deprecated Prefer GEMINI_TTS_VOICE_OPTIONS / voicesForSelection */
export const GEMINI_TTS_VOICES = GEMINI_TTS_VOICE_OPTIONS.map((v) => v.id);

/** @deprecated Prefer OPENAI_TTS_VOICE_OPTIONS / voicesForSelection */
export const OPENAI_TTS_VOICES = OPENAI_TTS_VOICE_OPTIONS.map((v) => v.id);

export function geminiSampleUrl(voiceId: string): string {
	return `${GEMINI_SAMPLE_BASE}/${voiceId}.wav`;
}

export function allVoiceOptions(
	provider: TtsVoiceProvider,
): readonly TtsVoiceOption[] {
	return provider === "gemini"
		? GEMINI_TTS_VOICE_OPTIONS
		: OPENAI_TTS_VOICE_OPTIONS;
}

export function voicesForSelection(input: {
	provider: TtsVoiceProvider;
	gender?: TtsVoiceGender | null;
}): TtsVoiceOption[] {
	const all = allVoiceOptions(input.provider);
	if (!input.gender) return [...all];
	return all.filter((voice) => voice.gender === input.gender);
}

export function voicesForProvider(
	provider: TtsVoiceProvider,
	gender?: TtsVoiceGender | null,
): string[] {
	return voicesForSelection({ provider, gender }).map((voice) => voice.id);
}

export function defaultVoiceId(
	provider: TtsVoiceProvider,
	gender: TtsVoiceGender,
): string {
	const matched = voicesForSelection({ provider, gender });
	if (matched[0]) return matched[0].id;
	return allVoiceOptions(provider)[0]?.id ?? "Aoede";
}

export function resolveVoiceIdForGender(input: {
	provider: TtsVoiceProvider;
	gender: TtsVoiceGender;
	currentVoiceId?: string | null;
}): string {
	const options = voicesForSelection({
		provider: input.provider,
		gender: input.gender,
	});
	if (
		input.currentVoiceId &&
		options.some((voice) => voice.id === input.currentVoiceId)
	)
		return input.currentVoiceId;
	return options[0]?.id ?? defaultVoiceId(input.provider, input.gender);
}

export function findVoiceOption(
	provider: TtsVoiceProvider,
	voiceId: string,
): TtsVoiceOption | undefined {
	return allVoiceOptions(provider).find((voice) => voice.id === voiceId);
}

function formatVoiceList(voices: readonly TtsVoiceOption[]): string {
	return voices
		.map((voice) =>
			voice.style ? `${voice.id} (${voice.style})` : voice.id,
		)
		.join(", ");
}

/** Prompt block — voices for the configured TTS provider only. */
export function formatTtsVoiceCatalogForPrompt(): string {
	const provider = getVideoProjectTtsProvider();
	const male = voicesForSelection({ provider, gender: "male" });
	const female = voicesForSelection({ provider, gender: "female" });
	const label = provider === "openai" ? "OpenAI" : "Gemini";

	return [
		`## NPC / character TTS voices (CRITICAL — provider is locked to ${provider})`,
		`- EVERY narrator.provider and character.voice.provider MUST be exactly "${provider}".`,
		`- Do NOT use ${provider === "gemini" ? "openai" : "gemini"} voices, ids, or provider strings at all.`,
		`- Every speaking character MUST set gender ("male"|"female") AND voice { provider: "${provider}", voiceId }.`,
		`- voiceId MUST be chosen from the ${label} gender list below — never invent names, never cross gender, never borrow the other vendor's catalog.`,
		"- Prefer unique voiceIds across characters when possible.",
		"- Match voice style tags to personality when possible.",
		"",
		`${label} male:`,
		formatVoiceList(male),
		`${label} female:`,
		formatVoiceList(female),
	].join("\n");
}

/**
 * Coerce a character voice onto the configured TTS provider + valid gender catalog.
 * Does not invent a voice when none is set — use ensureUniqueCharacterVoices for that.
 */
export function normalizeCharacterTtsVoice(input: {
	gender?: TtsVoiceGender | string | null;
	voice?: {
		provider?: string | null;
		voiceId?: string | null;
	} | null;
}): {
	gender?: TtsVoiceGender;
	voice?: { provider: TtsVoiceProvider; voiceId: string };
} {
	const provider = getVideoProjectTtsProvider();
	const rawVoiceId =
		typeof input.voice?.voiceId === "string"
			? input.voice.voiceId.trim()
			: "";

	const knownOnActive = rawVoiceId
		? findVoiceOption(provider, rawVoiceId)
		: undefined;
	const knownElsewhere = rawVoiceId
		? findVoiceOption(
				provider === "gemini" ? "openai" : "gemini",
				rawVoiceId,
			)
		: undefined;

	let gender: TtsVoiceGender | undefined =
		input.gender === "male" || input.gender === "female"
			? input.gender
			: undefined;
	if (!gender && knownOnActive) gender = knownOnActive.gender;
	if (!gender && knownElsewhere) gender = knownElsewhere.gender;
	if (!gender) gender = "male";

	if (!rawVoiceId) return { gender };

	const voiceId = resolveVoiceIdForGender({
		provider,
		gender,
		currentVoiceId: knownOnActive?.id ?? undefined,
	});

	return {
		gender,
		voice: { provider, voiceId },
	};
}

function hashStringSeed(input: string): number {
	let hash = 2166136261;
	for (let index = 0; index < input.length; index += 1) {
		hash ^= input.charCodeAt(index);
		hash = Math.imul(hash, 16777619);
	}
	return hash >>> 0;
}

function seededShuffleIds(ids: readonly string[], seed: string): string[] {
	const next = [...ids];
	let state = hashStringSeed(seed);
	for (let index = next.length - 1; index > 0; index -= 1) {
		state = (Math.imul(state, 1664525) + 1013904223) >>> 0;
		const swap = state % (index + 1);
		const current = next[index]!;
		next[index] = next[swap]!;
		next[swap] = current;
	}
	return next;
}

function pickUnusedVoiceId(
	pool: readonly string[],
	used: Set<string>,
	preferredStart: number,
): string | undefined {
	if (pool.length === 0) return undefined;
	for (let offset = 0; offset < pool.length; offset += 1) {
		const candidate = pool[(preferredStart + offset) % pool.length]!;
		if (!used.has(candidate)) return candidate;
	}
	return undefined;
}

export interface EnsureUniqueCharacterVoicesOptions {
	/** Stable seed so the same cast gets the same “random” assignment. */
	seed?: string;
}

/**
 * Assign a unique TTS voice per character (no shared voiceIds).
 * Keeps existing valid unique voices; fills gaps with a seeded shuffle.
 * Persists by returning a new characters map for the caller to save.
 */
export function ensureUniqueCharacterVoices<
	T extends {
		gender?: TtsVoiceGender | string | null;
		voice?: {
			provider?: string | null;
			voiceId?: string | null;
		} | null;
	},
>(
	characters: Record<string, T>,
	options?: EnsureUniqueCharacterVoicesOptions,
): Record<string, T> {
	const provider = getVideoProjectTtsProvider();
	const seed = options?.seed?.trim() || "video-project";
	const entries = Object.entries(characters).sort(([a], [b]) =>
		a.localeCompare(b),
	);
	if (entries.length === 0) return characters;

	const used = new Set<string>();
	const assigned = new Map<
		string,
		{ gender: TtsVoiceGender; voiceId: string }
	>();

	for (const [id, character] of entries) {
		const normalized = normalizeCharacterTtsVoice({
			gender: character.gender,
			voice: character.voice,
		});
		const gender = normalized.gender ?? "male";
		const voiceId = normalized.voice?.voiceId?.trim() ?? "";
		if (!voiceId || used.has(voiceId)) continue;
		used.add(voiceId);
		assigned.set(id, { gender, voiceId });
	}

	const malePool = seededShuffleIds(
		voicesForSelection({ provider, gender: "male" }).map((voice) => voice.id),
		`${seed}:male`,
	);
	const femalePool = seededShuffleIds(
		voicesForSelection({ provider, gender: "female" }).map(
			(voice) => voice.id,
		),
		`${seed}:female`,
	);
	const anyPool = seededShuffleIds(
		allVoiceOptions(provider).map((voice) => voice.id),
		`${seed}:any`,
	);

	for (const [id, character] of entries) {
		if (assigned.has(id)) continue;
		const normalized = normalizeCharacterTtsVoice({
			gender: character.gender,
			voice: character.voice,
		});
		const gender = normalized.gender ?? "male";
		const genderPool = gender === "female" ? femalePool : malePool;
		const preferred = hashStringSeed(`${seed}:${id}`) % Math.max(1, genderPool.length);
		const voiceId =
			pickUnusedVoiceId(genderPool, used, preferred) ??
			pickUnusedVoiceId(anyPool, used, preferred);
		if (!voiceId) {
			// Catalog exhausted — keep a gender default so TTS can still run.
			const fallback = defaultVoiceId(provider, gender);
			assigned.set(id, { gender, voiceId: fallback });
			continue;
		}
		used.add(voiceId);
		assigned.set(id, { gender, voiceId });
	}

	const next: Record<string, T> = {};
	for (const [id, character] of Object.entries(characters)) {
		const pick = assigned.get(id);
		if (!pick) {
			next[id] = character;
			continue;
		}
		next[id] = {
			...character,
			gender: pick.gender,
			voice: { provider, voiceId: pick.voiceId },
		};
	}
	return next;
}

export function characterVoicesNeedPersist(
	before: Record<string, { voice?: { voiceId?: string | null } | null }>,
	after: Record<string, { voice?: { voiceId?: string | null } | null }>,
): boolean {
	const beforeIds = Object.keys(before).sort();
	const afterIds = Object.keys(after).sort();
	if (beforeIds.length !== afterIds.length) return true;
	for (let index = 0; index < beforeIds.length; index += 1) {
		const id = beforeIds[index]!;
		if (id !== afterIds[index]) return true;
		const beforeVoice = before[id]?.voice?.voiceId?.trim() ?? "";
		const afterVoice = after[id]?.voice?.voiceId?.trim() ?? "";
		if (beforeVoice !== afterVoice) return true;
	}
	return false;
}
